/**
 * Dialog that lists every task belonging to a given headsection of a plot.
 *
 * Membership is resolved exactly like the Tehtävähallinta kanban: the PPs in
 * this plot whose `code` starts with the headsection number are collected, and
 * their names are sent to the backend as `procurementPackageNames`. The server
 * then matches every task linked to one of those PPs — directly or via a work
 * section — so the result set is identical to selecting the same PPs on the
 * kanban's procurement-package filter.
 *
 * Unlike the kanban, this view is intentionally agnostic of the effective
 * responsible user: no "Minun" / responsibleUserId constraint is applied, so
 * every task in the headsection is shown regardless of who owns it.
 *
 * Clicking a task card opens the same `TaskDialog` used from the Tehtävät
 * kanban, so editing flows are identical between the two surfaces.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  TaskStatus,
  type PlotWithStats,
  type TaskExtended,
} from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { GET_TASKS, buildGraphQLFilter, useTaskMutations } from "@/hooks/useTasks";
import { useTaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import { useProcurementPackages } from "@/hooks/useProcurementPackages";
import { TaskSummaryCard } from "@/components/tasks/TaskSummaryCard";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { useTeamsAuth } from "@/components/providers/TeamsAuthProvider";
import { useUsers } from "@/hooks/useUsers";
import { KANBAN_COLUMNS } from "@/lib/taskBoardMeta";
import { ScrollArea } from "@/components/ui/scroll-area";

interface HeadsectionTasksDialogProps {
  plot: PlotWithStats | null;
  headsectionNumber: number | null;
  /**
   * Project name for the plot. Used to populate the `projectNameById` map
   * passed down to `TaskSummaryCard` so the project line on each card
   * renders consistently with the kanban (where the same map is passed).
   */
  projectName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TasksQueryResult {
  tasks: {
    items: TaskExtended[];
    totalCount: number;
    hasNextPage: boolean;
    endCursor?: string;
  };
}

/**
 * Page size for the headsection task list. The dialog pages through every
 * result (see the fetchMore effect), so this only controls how many rows are
 * fetched per round-trip, not the total shown.
 */
const PAGE_SIZE = 500;

/**
 * Resolve the PP code a task belongs to, falling back through its section
 * when the task isn't linked to a PP directly.
 */
function getTaskPPCode(task: TaskExtended): string | null {
  return (
    task.procurementPackage?.code ??
    task.section?.procurementPackage?.code ??
    null
  );
}

export function HeadsectionTasksDialog({
  plot,
  headsectionNumber,
  projectName,
  open,
  onOpenChange,
}: HeadsectionTasksDialogProps) {
  // Resolve the headsection's procurement packages → their names, mirroring
  // the kanban's PP filter. Membership = PPs in this plot whose code starts
  // with the headsection number (the backend uses the same first-digit rule
  // when bucketing tasks into headsections).
  const { procurementPackages, loading: ppsLoading } = useProcurementPackages();

  const headsectionPpNames = useMemo(() => {
    if (!plot || headsectionNumber == null) return [];
    const prefix = String(headsectionNumber);
    const names = new Set<string>();
    for (const pp of procurementPackages) {
      if (pp.plotId !== plot.id) continue;
      if (!pp.code || !pp.code.startsWith(prefix)) continue;
      if (pp.name) names.add(pp.name);
    }
    return [...names];
  }, [procurementPackages, plot, headsectionNumber]);

  // Same server filter the kanban builds, scoped to this plot and these PPs.
  // Deliberately omits any responsible-user constraint so the view is agnostic
  // of who owns each task.
  const taskFilter = useMemo(
    () =>
      buildGraphQLFilter({
        plotIds: plot ? [plot.id] : [],
        procurementPackageNames: headsectionPpNames,
        parentIdIsNull: true,
      }),
    [plot, headsectionPpNames],
  );

  // Gate the query until the dialog is open with a real target and we have at
  // least one PP name to match — sending an empty name list would drop the PP
  // constraint and return every task in the plot.
  const shouldQuery =
    open && !!plot && !ppsLoading && headsectionPpNames.length > 0;

  const { data, loading, refetch, fetchMore } = useQuery<TasksQueryResult>(
    GET_TASKS,
    {
      variables: { limit: PAGE_SIZE, filter: taskFilter },
      skip: !shouldQuery,
      fetchPolicy: "cache-and-network",
    },
  );

  // Page through every result instead of capping the list: whenever the
  // current data reports another page, fetch it. The Apollo cache merges
  // pages (keyed by `filter`), so `data.tasks.items` accumulates the full
  // set. The ref guards against firing a second request before the in-flight
  // one resolves.
  const loadingMoreRef = useRef(false);
  useEffect(() => {
    if (!shouldQuery) return;
    const conn = data?.tasks;
    if (!conn?.hasNextPage || !conn.endCursor) return;
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    fetchMore({
      variables: { limit: PAGE_SIZE, after: conn.endCursor, filter: taskFilter },
    }).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [data, shouldQuery, fetchMore, taskFilter]);

  // The server has already applied the headsection/plot filter, so the items
  // are used as-is (no client-side narrowing).
  const filteredTasks = useMemo(
    () => data?.tasks.items ?? [],
    [data],
  );

  // Show the skeleton while the PP list or the task query is still resolving;
  // only fall through to "Ei tehtäviä" once both have settled.
  const isLoading = open && !!plot && (ppsLoading || loading);

  const groupedTasks = useMemo(() => {
    const sortByDeadline = (tasks: TaskExtended[]) =>
      [...tasks].sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      });
    return KANBAN_COLUMNS.map((col) => {
      const statuses = col.statuses ?? [col.status];
      return { ...col, tasks: sortByDeadline(filteredTasks.filter((t) => statuses.includes(t.status))) };
    });
  }, [filteredTasks]);

  // PP codes are derived from the filtered tasks themselves — shows the user
  // which packages the headsection actually covers in this plot.
  const ppCodesLabel = useMemo(() => {
    const codes = new Set<string>();
    for (const t of filteredTasks) {
      const c = getTaskPPCode(t);
      if (c) codes.add(c);
    }
    return [...codes].sort().join(", ");
  }, [filteredTasks]);

  // --- Task detail dialog (reuses the kanban's TaskDialog) ---
  const [selectedTask, setSelectedTask] = useState<TaskExtended | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  const { user } = useTeamsAuth();
  const { users } = useUsers();
  const currentUserId = useMemo(() => {
    if (!user?.id) return "";
    return users.find((u) => u.azureObjectId === user.id)?.id || "";
  }, [user?.id, users]);

  const { updateTaskStatus } = useTaskMutations();
  const toggleTaskCompleted = useTaskCompletionToggle(refetch);

  const openTask = useCallback((task: TaskExtended) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
  }, []);

  const handleTaskDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      setTaskDialogOpen(nextOpen);
      // When the detail dialog closes, refresh the list so status/metadata
      // edits made inside it are reflected in the cards behind it.
      if (!nextOpen) refetch();
    },
    [refetch],
  );

  const handleUpdateStatus = useCallback(
    async (taskId: string, status: TaskStatus) => {
      try {
        if (selectedTask?.id === taskId && selectedTask.version != null) {
          await updateTaskStatus(taskId, status, selectedTask.version);
          await refetch();
        }
        setTaskDialogOpen(false);
      } catch (error) {
        console.error("Failed to update task status:", error);
      }
    },
    [selectedTask, updateTaskStatus, refetch],
  );

  // One-entry maps scoped to the dialog's plot/project so every task card
  // gets the same project/location treatment as on the kanban. Without
  // these, the project line is always skipped and the location line falls
  // to blank whenever the task's own `location` is missing a hierarchy.
  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (plot && projectName) map.set(plot.projectId, projectName);
    return map;
  }, [plot, projectName]);

  const plotNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (plot) map.set(plot.id, plot.name);
    return map;
  }, [plot]);

  const titleSuffix = plot?.name ? ` – ${plot.name}` : "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[1100px] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {headsectionNumber != null
                ? `Lohko ${headsectionNumber}`
                : "Lohko"}
              {titleSuffix}
            </DialogTitle>
            <DialogDescription>
              {ppCodesLabel ? (
                <>
                  Hankintapaketit:{" "}
                  <span className="font-mono">{ppCodesLabel}</span>
                </>
              ) : (
                "Ei tehtäviä tälle lohkolle"
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 pt-3 overflow-hidden">
            {isLoading ? (
              <div className="grid grid-cols-3 gap-4 h-full">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    {[1, 2].map((j) => (
                      <Skeleton key={j} className="h-32 w-full" />
                    ))}
                  </div>
                ))}
              </div>
            ) : filteredTasks.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Ei tehtäviä
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4 h-full">
                {groupedTasks.map((col) => (
                  <div key={col.status} className="flex flex-col overflow-hidden">
                    <div className="flex items-center gap-2 mb-3 shrink-0">
                      <div className={`h-3 w-3 rounded-full ${col.color}`} />
                      <h3 className="text-sm font-semibold">{col.label}</h3>
                      <span className="text-xs text-muted-foreground">({col.tasks.length})</span>
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-3 pb-2">
                      {col.tasks.length === 0 ? (
                        <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                          Ei tehtäviä
                        </div>
                      ) : (
                        col.tasks.map((task) => (
                          <TaskSummaryCard
                            key={task.id}
                            task={task}
                            projectNameById={projectNameById}
                            plotNameById={plotNameById}
                            onClick={() => openTask(task)}
                            onToggleCompleted={toggleTaskCompleted}
                          />
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="p-0"/>
        </DialogContent>
      </Dialog>

      {/* Task detail — rendered alongside, not inside, the list dialog so
          Radix stacks them predictably (close detail → list stays open). */}
      <TaskDialog
        task={selectedTask}
        open={taskDialogOpen}
        onOpenChange={handleTaskDialogOpenChange}
        onUpdateStatus={handleUpdateStatus}
        currentUserId={currentUserId}
        onTaskUpdated={setSelectedTask}
        onSubtasksChanged={refetch}
      />
    </>
  );
}
